import useEmblaCarousel from 'embla-carousel-react';
import React, { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import { cx } from 'react-twc';

import SmallChip from '@/components/Chip/SmallChip';
import Image from '@/components/v2/Image';

import { sample } from '../../_const/sample';
import { useDotButton } from './DotButton';

type SampleReviewCarouselProps = {
  slides: (typeof sample)[];
  setShopName: (shopName: string) => void;
  setRating: (rating: number) => void;
  setReview: (review: string) => void;
  setUserName: (userName: string) => void;
  setWritable: () => void;
  setUnwritable: () => void;
  review: string;
};

const SampleReviewCarousel = ({
  slides,
  review,
  setShopName,
  setRating,
  setReview,
  setUserName,
  setWritable,
  setUnwritable,
}: SampleReviewCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const { selectedIndex, onDotButtonClick } = useDotButton(emblaApi);
  const [testShopName, setTestShopName] = useState('');

  const onNextButtonClick = () => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  };

  const onPrevButtonClick = () => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  };

  useEffect(() => {
    setShopName(slides[selectedIndex].shopName);
    setRating(slides[selectedIndex].rating);
    setReview(slides[selectedIndex].review);
    setUserName(slides[selectedIndex].userName);
    setUnwritable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  const handleClickDot = (e: MouseEvent<HTMLButtonElement>) => {
    const index = e.currentTarget.dataset.index;

    if (index === undefined) return;
    onDotButtonClick(Number(index));
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setReview(e.target.value);
  };

  const handleFocus = () => {
    setWritable();
  };

  const handleChangeTestShopName = (e: ChangeEvent<HTMLInputElement>) => {
    setTestShopName(e.target.value);
    setShopName(e.target.value);
  };

  return (
    <div
      className={cx(
        'border border-gray-5 rounded-xl h-full min-h-[240px] w-full relative px-8 md:px-12 py-6 overflow-hidden',
        // isFocus && isWritable ? 'border-secondary ring-1 ring-secondary' : '',
      )}
    >
      <div ref={emblaRef} className={'embla overflow-hidden h-full'}>
        <div className="embla__container flex h-full">
          {slides.map((slide, idx) => {
            if (!slide) return <></>;
            return (
              <div key={'slide' + idx} className="embla__slide min-w-0 w-full flex-none h-full">
                <div className="h-full">
                  {slide.shopName === '' ? (
                    <input
                      type="text"
                      value={testShopName}
                      onChange={handleChangeTestShopName}
                      className="py-1 px-3.5 text-xs sm:text-lg font-medium bg-gray-7 rounded-full"
                      placeholder="매장명을 입력해주세요"
                    />
                  ) : (
                    <SmallChip>{slide.shopName}</SmallChip>
                  )}

                  <div className="mt-2 min-h-[120px]">
                    <textarea
                      className="w-full min-h-[120px] h-full resize-none focus-within:outline-none"
                      value={review}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      placeholder="댓글을 생성해 보고싶은 리뷰 내용을 직접 입력해 보세요!"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="absolute left-0 top-0 h-full">
        <button
          onClick={onPrevButtonClick}
          color="gray"
          className={cx('h-full px-0 md:px-2', 'hover:bg-tonal-secondary')}
        >
          <Image src="/img/icons/chevron_left_wide.svg" width={30} height={60} alt="좌" />
        </button>
      </div>

      <div className="absolute right-0 top-0 h-full">
        <button
          onClick={onNextButtonClick}
          color="gray"
          className={cx(
            'h-full px-0 md:px-2 ',

            'hover:bg-tonal-secondary',
          )}
        >
          <Image src="/img/icons/chevron_right_wide.svg" width={30} height={60} alt="우" />
        </button>
      </div>

      <div className="absolute left-0 bottom-4 flex w-full justify-center">
        <div className="flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              data-index={index}
              onClick={handleClickDot}
              className={cx(
                'w-5 h-2 md:w-8',
                'bg-black rounded-full border-secondary border',
                selectedIndex === index ? 'bg-secondary' : 'bg-white',
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SampleReviewCarousel;
